import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext, closestCorners, PointerSensor, useSensor, useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus, LayoutGrid, Calendar, BarChart3, Loader2, AlertCircle, Rocket } from 'lucide-react';
import { loggedGet, loggedMutation, apiClient } from './loggedFetch';
import logger from '../../lib/logger';
import InitiativeCard from './InitiativeCard';
import InitiativeTimeline from './InitiativeTimeline';
import InitiativeModal from './InitiativeModal';
import './StrategicInitiatives.css';

const STATUS_COLUMNS = [
  { key: 'planning', label: 'Planning' },
  { key: 'active', label: 'Active' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost / Abandoned' },
];

function formatCurrency(num) {
  if (num == null) return '$0';
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function DroppableColumn({ column, children, count }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col-${column.key}`,
    data: { statusKey: column.key },
  });

  return (
    <div
      ref={setNodeRef}
      className={`si-column ${isOver ? 'is-over' : ''}`}
    >
      <div className="si-column__header">
        <span>{column.label}</span>
        <span className="si-column__count">{count}</span>
      </div>
      <div className="si-column__cards">
        {children}
      </div>
    </div>
  );
}

function StrategicInitiatives({ vendor }) {
  const queryClient = useQueryClient();
  const [view, setView] = useState('board');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingInitiative, setEditingInitiative] = useState(null);

  // Sensors for drag-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // Fetch initiatives
  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-analysis', 'initiatives', vendor],
    queryFn: loggedGet(
      'StrategicInitiatives:fetch',
      `/api/vendor-analysis/${encodeURIComponent(vendor)}/initiatives`
    ),
    enabled: !!vendor,
    staleTime: 30000,
  });

  const initiatives = data?.initiatives || [];

  // Group by status for kanban
  const initiativesByStatus = useMemo(() => {
    const grouped = {
      planning: [],
      active: [],
      won: [],
      lost: [],
    };
    for (const init of initiatives) {
      // Combine 'lost' and 'abandoned' into the 'lost' column
      const key = init.status === 'abandoned' ? 'lost' : init.status;
      if (grouped[key]) {
        grouped[key].push(init);
      }
    }
    return grouped;
  }, [initiatives]);

  // Metrics calculations
  const metrics = useMemo(() => {
    const active = initiatives.filter(i => i.status === 'active' || i.status === 'planning');
    const completed = initiatives.filter(i => i.status === 'won' || i.status === 'lost' || i.status === 'abandoned');
    const won = initiatives.filter(i => i.status === 'won');

    const totalTargetValue = initiatives.reduce((sum, i) => sum + (i.target_value || 0), 0);
    const pipelineValue = active.reduce((sum, i) => sum + (i.target_value || 0), 0);
    const winRate = completed.length > 0
      ? Math.round((won.length / completed.length) * 100)
      : 0;

    return {
      totalTargetValue,
      activeCount: active.length,
      winRate,
      pipelineValue,
    };
  }, [initiatives]);

  // Mutations — optimistic: patch the cache immediately (drag/save/delete feel
  // instant), roll back on error, reconcile with the server on settle.
  const initiativesKey = ['vendor-analysis', 'initiatives', vendor];

  const createMutation = useMutation({
    mutationFn: loggedMutation(
      'StrategicInitiatives:create',
      (payload) => apiClient.post(
        `/api/vendor-analysis/${encodeURIComponent(vendor)}/initiatives`,
        payload
      )
    ),
    onMutate: async (payload) => {
      const previous = queryClient.getQueryData(initiativesKey);
      if (previous) {
        queryClient.setQueryData(initiativesKey, {
          ...previous,
          initiatives: [
            { id: `temp-${Date.now()}`, status: 'planning', ...payload },
            ...(previous.initiatives || []),
          ],
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(initiativesKey, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: initiativesKey });
    },
  });

  const updateMutation = useMutation({
    mutationFn: loggedMutation(
      'StrategicInitiatives:update',
      ({ id, payload }) => apiClient.put(
        `/api/vendor-analysis/${encodeURIComponent(vendor)}/initiatives/${id}`,
        payload
      )
    ),
    onMutate: async ({ id, payload }) => {
      const previous = queryClient.getQueryData(initiativesKey);
      if (previous) {
        queryClient.setQueryData(initiativesKey, {
          ...previous,
          initiatives: (previous.initiatives || []).map(i =>
            i.id === id ? { ...i, ...payload } : i
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(initiativesKey, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: initiativesKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: loggedMutation(
      'StrategicInitiatives:delete',
      (id) => apiClient.del(
        `/api/vendor-analysis/${encodeURIComponent(vendor)}/initiatives/${id}`
      )
    ),
    onMutate: async (id) => {
      const previous = queryClient.getQueryData(initiativesKey);
      if (previous) {
        queryClient.setQueryData(initiativesKey, {
          ...previous,
          initiatives: (previous.initiatives || []).filter(i => i.id !== id),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(initiativesKey, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: initiativesKey });
    },
  });

  const handleSave = useCallback(async (payload, id) => {
    if (id) {
      await updateMutation.mutateAsync({ id, payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  }, [createMutation, updateMutation]);

  const handleDelete = useCallback(async (id) => {
    await deleteMutation.mutateAsync(id);
  }, [deleteMutation]);

  const handleCardClick = (initiative) => {
    setEditingInitiative(initiative);
    setModalOpen(true);
  };

  const handleNewInitiative = () => {
    setEditingInitiative(null);
    setModalOpen(true);
  };

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Check if dropping on a column
    if (overId.toString().startsWith('col-')) {
      const newStatus = over.data.current?.statusKey;
      if (!newStatus) return;

      const initiative = initiatives.find(i => i.id === activeId);
      if (!initiative || initiative.status === newStatus) return;

      // Handle 'lost' column mapping back to 'lost' or keep 'abandoned'
      const actualStatus = newStatus === 'lost' ? 'lost' : newStatus;

      logger.vendorAnalysis.info('StrategicInitiatives:drag-status-change', {
        id: activeId, from: initiative.status, to: actualStatus,
      });

      updateMutation.mutate({
        id: activeId,
        payload: { status: actualStatus },
      });
    }
  }, [initiatives, updateMutation]);

  if (isLoading) {
    return (
      <div className="si-loading">
        <Loader2 size={24} className="vca-spin" />
        <span style={{ marginLeft: 8 }}>Loading initiatives...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="si-error">
        <AlertCircle size={24} />
        <span>Failed to load initiatives: {error.message}</span>
      </div>
    );
  }

  return (
    <div className="si-container">
      <div className="si-header">
        <div className="si-view-toggle">
          <button
            className={`si-view-btn ${view === 'board' ? 'is-active' : ''}`}
            onClick={() => setView('board')}
          >
            <LayoutGrid size={14} />
            <span>Board</span>
          </button>
          <button
            className={`si-view-btn ${view === 'timeline' ? 'is-active' : ''}`}
            onClick={() => setView('timeline')}
          >
            <Calendar size={14} />
            <span>Timeline</span>
          </button>
          <button
            className={`si-view-btn ${view === 'metrics' ? 'is-active' : ''}`}
            onClick={() => setView('metrics')}
          >
            <BarChart3 size={14} />
            <span>Metrics</span>
          </button>
        </div>
        {/* Initiatives are user-authored — the shell time filter cannot
            scope them, so say so instead of implying it does. */}
        <span className="si-nofilter-note">Not affected by the global time filter</span>
        <button className="si-add-btn" onClick={handleNewInitiative}>
          <Plus size={16} />
          <span>New Initiative</span>
        </button>
      </div>

      {initiatives.length === 0 && view !== 'metrics' ? (
        <div className="si-empty">
          <Rocket size={48} className="si-empty__icon" />
          <h3 className="si-empty__title">No initiatives yet</h3>
          <p className="si-empty__text">
            Create your first strategic initiative to start planning takeouts, expansions, or defense plays for {vendor}.
          </p>
          <button className="si-add-btn" onClick={handleNewInitiative}>
            <Plus size={16} />
            <span>Create First Initiative</span>
          </button>
        </div>
      ) : (
        <>
          {view === 'board' && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragEnd={handleDragEnd}
            >
              <div className="si-board">
                {STATUS_COLUMNS.map(col => {
                  const colInitiatives = initiativesByStatus[col.key] || [];
                  return (
                    <SortableContext
                      key={col.key}
                      items={colInitiatives.map(i => i.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <DroppableColumn column={col} count={colInitiatives.length}>
                        {colInitiatives.map(init => (
                          <InitiativeCard
                            key={init.id}
                            initiative={init}
                            onClick={handleCardClick}
                          />
                        ))}
                      </DroppableColumn>
                    </SortableContext>
                  );
                })}
              </div>
            </DndContext>
          )}

          {view === 'timeline' && (
            <InitiativeTimeline
              initiatives={initiatives}
              onSelect={handleCardClick}
            />
          )}

          {view === 'metrics' && (
            <div className="si-metrics">
              <div className="si-metric-card">
                <div className="si-metric-card__label">Total Target Value</div>
                <div className="si-metric-card__value">
                  {formatCurrency(metrics.totalTargetValue)}
                </div>
                <div className="si-metric-card__sub">
                  Across {initiatives.length} initiative{initiatives.length !== 1 ? 's' : ''}
                </div>
              </div>

              <div className="si-metric-card si-metric-card--accent">
                <div className="si-metric-card__label">Active Initiatives</div>
                <div className="si-metric-card__value">{metrics.activeCount}</div>
                <div className="si-metric-card__sub">Planning + Active</div>
              </div>

              <div className="si-metric-card si-metric-card--success">
                <div className="si-metric-card__label">Win Rate</div>
                <div className="si-metric-card__value">{metrics.winRate}%</div>
                <div className="si-metric-card__sub">Of completed initiatives</div>
              </div>

              <div className="si-metric-card">
                <div className="si-metric-card__label">Pipeline Value</div>
                <div className="si-metric-card__value">
                  {formatCurrency(metrics.pipelineValue)}
                </div>
                <div className="si-metric-card__sub">In active pipeline</div>
              </div>
            </div>
          )}
        </>
      )}

      <InitiativeModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingInitiative(null);
        }}
        initiative={editingInitiative}
        vendor={vendor}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  );
}

export default StrategicInitiatives;
